import React, { FC } from 'react'
import { CellularPayPrompt } from './types'
import { Banner } from './banner'
import { PromptButton } from './dialog-parts'
import { formatDate, formatMoney } from './format'
import { affectedSentence, isSuspendedStage, payableSimStage, simStageMessage } from './sim-status'
import { achEndedMessage, endedAch } from './ach'

interface DueBannerProps {
  prompt: CellularPayPrompt
  onPay: () => void
  onDetails: () => void
}

const amountDue = (prompt: CellularPayPrompt): string =>
  `Cellular service payment due: ${formatMoney(prompt.amountDueUsd, prompt.currency)} ` +
  `for ${prompt.controllers} display(s)` +
  `${prompt.dueDate ? `, due ${formatDate(prompt.dueDate)}` : ''}.`

// Once a SIM is past due the stage says more than the amount; once one is off,
// the customer also needs to know which signs went dark.
export const dueBannerMessage = (prompt: CellularPayPrompt): string => {
  // A bank payment that failed says why first: the customer thinks it is paid.
  if (endedAch(prompt)) return `${achEndedMessage(prompt)} ${amountDue(prompt)}`
  const stage = payableSimStage(prompt)
  if (!stage) return amountDue(prompt)
  if (!isSuspendedStage(stage)) return simStageMessage(prompt)
  return [simStageMessage(prompt), affectedSentence(prompt)].filter(Boolean).join(' ')
}

// A renewal's only reminder (it never pops up by itself), and a snoozed back-bill's
// way back to the bill. Red, not amber, once a SIM has been switched off.
export const DueBanner: FC<DueBannerProps> = ({ prompt, onPay, onDetails }) => (
  <Banner
    message={dueBannerMessage(prompt)}
    tone={isSuspendedStage(payableSimStage(prompt)) ? 'destructive' : 'warning'}
  >
    <PromptButton
      onClick={onDetails}
      variant='textPrimary'
    >
      Details
    </PromptButton>
    <PromptButton
      onClick={onPay}
      variant='textPrimary'
    >
      Pay Now
    </PromptButton>
  </Banner>
)
